/**
 * Pasif öğrenci: a learner the school has not opened the platform for. The backend refuses their token with the
 * bare-string key {@link LEARNER_PROFILE_DEACTIVATED_KEY} on every path (profile pick, refresh, K12 / SEBIT
 * login); the app answers with the full-page PROFILE_INACTIVE screen instead of a toast or the picker.
 */
export const LEARNER_PROFILE_DEACTIVATED_KEY = "learnerProfileIsDeactivated";

const CONTEXT_STORAGE_KEY = "deactivatedProfileContext";

export interface DeactivatedProfileContext {
  institutionName?: string;
  branchName?: string;
}

/** True when a failed request is the backend's "this learner profile is deactivated" refusal. */
export const isLearnerProfileDeactivatedError = (err: unknown): boolean =>
  (err as any)?.response?.data === LEARNER_PROFILE_DEACTIVATED_KEY;

/** The school and class to name on the screen, from a learner profile DTO. Missing parts are left out. */
export const buildDeactivatedProfileContext = (userProfileDTO: any): DeactivatedProfileContext => {
  const branchDTO = userProfileDTO?.branchDTO;
  const institutionName = branchDTO?.gradeDTO?.seasonDTO?.institutionDTO?.name;
  const branchName = branchDTO?.name;
  return {
    ...(institutionName ? { institutionName } : {}),
    ...(branchName ? { branchName } : {}),
  };
};

/**
 * Remembered for the screen only (sessionStorage: this tab, this visit). Best-effort: a browser that refuses
 * storage just gets the screen without the school line.
 */
export const rememberDeactivatedProfile = (userProfileDTO: any): void => {
  try {
    window.sessionStorage.setItem(
      CONTEXT_STORAGE_KEY,
      JSON.stringify(buildDeactivatedProfileContext(userProfileDTO))
    );
  } catch (ignored) {
    // storage unavailable — the screen still renders
  }
};

export const forgetDeactivatedProfile = (): void => {
  try {
    window.sessionStorage.removeItem(CONTEXT_STORAGE_KEY);
  } catch (ignored) {
    // nothing to forget
  }
};

export const readDeactivatedProfileContext = (): DeactivatedProfileContext => {
  try {
    const raw = window.sessionStorage.getItem(CONTEXT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (ignored) {
    return {};
  }
};

/** "Zafer Koleji · 11/D", "11/D", or "" when nothing is known. */
export const formatDeactivatedProfileContext = (context: DeactivatedProfileContext): string =>
  [context?.institutionName, context?.branchName].filter(Boolean).join(" · ");
